import { useState, useEffect } from "react";
import "./index.css";
import { Provider } from "@/components/ui/provider";
import { ColorModeButton } from "@/components/ui/color-mode";
import {
  Box,
  HStack,
  Heading,
  Flex,
  Button,
  Drawer,
  IconButton,
  VStack,
  useBreakpointValue,
  Portal,
  CloseButton,
} from "@chakra-ui/react";
import { LuMenu } from "react-icons/lu";

// Import page components
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { SearchPage } from "./pages/Search";
import { EpisodePage } from "./pages/Episode";
import { EpisodesPage } from "./pages/Episodes";
import { NavSearch } from "./components/NavSearch";
import { SearchModal } from "./components/SearchModal";

// A simple function to render the correct page based on the URL path.
export function Router() {
  // Normalize path: lowercase, strip all trailing slashes
  const rawPath = window.location.pathname || "/";
  const path = rawPath.toLowerCase().replace(/\/+$/, "") || "/";
  
  console.log(`[Router] raw="${rawPath}" normalized="${path}"`);

  // Exact matches
  if (path === "/") {
    return <Home />;
  }
  if (path === "/about") {
    return <About />;
  }
  if (path === "/episodes") {
    return <EpisodesPage />;
  }
  
  // Prefix matches
  if (path.startsWith("/search")) {
    return <SearchPage key={window.location.search} />;
  }

  if (path.startsWith("/episode/")) {
    const match = path.match(/\/episode\/(\d+)/);
    if (match) {
      return <EpisodePage episodeNumber={match[1]} />;
    }
  }

  // Fallback
  console.log(`[Router] No match for "${path}", falling back to Home`);
  return <Home />;
}

function Layout() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const isDesktop = useBreakpointValue({ base: false, md: true });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setIsSearchOpen((isOpen) => !isOpen);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <>
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />

      <Flex
        direction="column"
        w="full"
        minH="100vh"
        align="center"
        bg="bg.muted"
        color="fg.muted"
      >
        {/* Header */}
        <Box as="header" w="full" py={4} px={8} borderBottomWidth="1px" top={0}>
          <HStack as="nav" justify="space-between" align="center">
            <Button variant="ghost" asChild>
              <a href="/">
                <Heading size="md">Everything Is Everything!</Heading>
              </a>
            </Button>
            {isDesktop ? (
              <HStack align="center">
                <NavSearch onClick={() => setIsSearchOpen(true)} />
                <Button variant="ghost" asChild>
                  <a href="/">Popular</a>
                </Button>
                <Button variant="ghost" asChild>
                  <a href="/episodes/">Episodes</a>
                </Button>
                <Button variant="ghost" asChild>
                  <a href="/about/">About</a>
                </Button>
                <ColorModeButton />
              </HStack>
            ) : (
              <Drawer.Root
                open={isDrawerOpen}
                onOpenChange={(e) => setDrawerOpen(e.open)}
                placement="end"
              >
                <HStack>
                  <NavSearch onClick={() => setIsSearchOpen(true)} />
                  <ColorModeButton />
                  <Drawer.Trigger asChild>
                    <IconButton aria-label="Open menu" variant="ghost">
                      <LuMenu />
                    </IconButton>
                  </Drawer.Trigger>
                </HStack>
                <Portal>
                  <Drawer.Backdrop />
                  <Drawer.Positioner>
                    <Drawer.Content bg="bg.canvas" color="fg.default">
                      <Drawer.Header>
                        <Drawer.Title>Navigation</Drawer.Title>
                      </Drawer.Header>
                      <Drawer.Body>
                        <VStack as="nav" align="stretch">
                          <Button
                            variant="ghost"
                            justifyContent="start"
                            asChild
                          >
                            <a href="/">Popular</a>
                          </Button>
                          <Button
                            variant="ghost"
                            justifyContent="start"
                            asChild
                          >
                            <a href="/episodes/">Episodes</a>
                          </Button>
                          <Button
                            variant="ghost"
                            justifyContent="start"
                            asChild
                          >
                            <a href="/about/">About</a>
                          </Button>
                        </VStack>
                      </Drawer.Body>
                      <Drawer.CloseTrigger asChild>
                        <CloseButton />
                      </Drawer.CloseTrigger>
                    </Drawer.Content>
                  </Drawer.Positioner>
                </Portal>
              </Drawer.Root>
            )}
          </HStack>
        </Box>

        {/* Main Content */}
        <Box as="main" w="full" maxW="4xl" mx="auto" p={8} flex="1">
          <Router />
        </Box>
      </Flex>
    </>
  );
}

export function App() {
  return (
    <Provider>
      <Layout />
    </Provider>
  );
}

export default App;
